/**
 * Routine の締めの確定の明細を、Routine の PDF と同じ形で出す(2026-10-23 発注者「実際の PDF 等を反映させられないか」→ 両方:
 * まず Gate で同じ形を作る。Routine の PDF そのもの(原本)は、Routine の改修で送るようになったら documents に入る)。
 *  - ①請求: 作業内訳(日付 × ドライバー。拠点ごとのまとまり・計・稼働日数・合計)+ ガソリン折半 + 表の下の行。スマホはドライバーごとの一覧
 *  - ②下請けの支払い: 「〇〇 御中 支払い明細」(ドライバー・稼働数・報酬・相殺・ペナルティ・支払額・支払い期日)
 *  - ③ドライバーの支払い: 「御支払通知書」(月度・宛名・差出人・御支払金額・明細・差引支払金額。行の並びは Routine の通知書と同じ)
 * 差出人(会社名・住所)はマスタ管理の会社(既定の会社)から出す。日付は Routine で確定した日
 * 使う場所: Routine の締めの明細(RoutineClosingDetailModal)・印刷ページ(RoutineClosingPrintPage)・支払いの詳細(ExternalDetail)
 */
import { Fragment } from 'react';
import { formatYen } from '@core/lib/money';
import { toShortDate } from '@core/lib/date';
import type { Company, RoutineBillingDetail, RoutineClosing, RoutineDriverDetail, RoutineSubcontractDetail } from '@core/lib/types';
import { billingTotal, monthDays, monthLabel } from './routineClosing';

export type Issuer = { name: string; postal_code: string | null; address: string | null };

/** 差出人: 既定の会社(無ければ最初の会社)の正式名称・住所 */
export function issuerFromCompanies(companies: Array<Pick<Company, 'name' | 'is_default'> & Partial<Pick<Company, 'legal_name' | 'postal_code' | 'address'>>>): Issuer | null {
  const c = companies.find((x) => x.is_default) ?? companies[0];
  return c ? { name: c.legal_name || c.name, postal_code: c.postal_code ?? null, address: c.address ?? null } : null;
}

/** '2026-09' → '令和8年9月' */
export function reiwaMonthLabel(month: string): string {
  return `令和${Number(month.slice(0, 4)) - 2018}年${Number(month.slice(5, 7))}月`;
}

/** ISO の日時 → 'YYYY-MM-DD'(JST) */
export function jstDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

const minus = (n: number) => (n === 0 ? '0' : `▲${formatYen(n)}`);

export default function RoutineStatement({ closing, issuer }: { closing: RoutineClosing; issuer: Issuer | null }) {
  const d = closing.detail;
  const issueDate = jstDate(closing.confirmed_at);
  if (d.kind === 'billing') return <BillingView month={closing.month} name={closing.name} d={d} />;
  if (d.kind === 'subcontract') return <SubcontractView month={closing.month} name={closing.name} d={d} issueDate={issueDate} />;
  if (d.kind === 'driver') return <DriverNotice month={closing.month} name={closing.name} d={d} issuer={issuer} issueDate={issueDate} />;
  return <p className="empty">燃料の振り分けの明細は次の段で作ります</p>;
}

function BillingView({ month, name, d }: { month: string; name: string; d: RoutineBillingDetail }) {
  const total = billingTotal(d);
  return (
    <div>
      <p style={{ margin: '0 0 2px', fontWeight: 700 }}>{name}様 作業内訳</p>
      <p style={{ margin: '0 0 8px' }}>{reiwaMonthLabel(month)}</p>
      {/* 画面の幅で切り替える(CSS)。印刷は幅に関係なく日ごとの表 */}
      <div className="rt-wide">
        <BillingMatrix month={month} d={d} />
      </div>
      <div className="rt-narrow">
        <BillingList d={d} />
      </div>

      {d.gas.length > 0 && (
        <>
          <p style={{ margin: '14px 0 6px', fontWeight: 700 }}>ガソリン代(折半)</p>
          <table>
            <thead>
              <tr>
                <th>拠点</th>
                <th>ドライバー</th>
                <th>コース</th>
                <th className="num">かかった金額</th>
                <th className="num">請求額(半額)</th>
              </tr>
            </thead>
            <tbody>
              {d.gas.map((g) => (
                <tr key={`${g.location}${g.driver_name}`}>
                  <td>{g.location}</td>
                  <td>{g.driver_name}</td>
                  <td>{g.course}</td>
                  <td className="num">{formatYen(g.amount)}</td>
                  <td className="num">{formatYen(g.half)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {d.extras.length > 0 && (
        <>
          <p style={{ margin: '14px 0 6px', fontWeight: 700 }}>表の下の行(集荷・固定額)</p>
          <table>
            <thead>
              <tr>
                <th>名称</th>
                <th className="num">単価</th>
                <th className="num">数量</th>
                <th className="num">金額</th>
              </tr>
            </thead>
            <tbody>
              {d.extras.map((x) => (
                <tr key={x.name}>
                  <td>{x.name}</td>
                  <td className="num">{x.unit_price === null ? '—' : formatYen(x.unit_price)}</td>
                  <td className="num">{x.quantity === null ? '—' : `${x.quantity}${x.quantity_unit ?? ''}`}</td>
                  <td className="num">{formatYen(x.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p style={{ margin: '14px 0 0', textAlign: 'right', fontWeight: 700, fontSize: 15 }}>請求額の合計(税込) {formatYen(total)}円</p>
    </div>
  );
}

/** PC: 日付 × ドライバーの表(作業内訳 PDF の形) */
function BillingMatrix({ month, d }: { month: string; d: RoutineBillingDetail }) {
  const days = monthDays(month);
  const cols = d.locations.flatMap((l) => l.columns.map((c) => ({ loc: l.location, c })));
  return (
    <table className="no-stack routine-matrix">
      <thead>
        <tr>
          <th rowSpan={3}>日付</th>
          <th rowSpan={3}>曜日</th>
          {d.locations.map((l) => (
            <th key={l.location} colSpan={l.columns.length} className="center">
              【{l.location}】
            </th>
          ))}
        </tr>
        <tr>
          {cols.map(({ loc, c }, i) => (
            <th key={`${loc}${i}`} className="center">
              {c.course}
            </th>
          ))}
        </tr>
        <tr>
          {cols.map(({ loc, c }, i) => (
            <th key={`${loc}${i}`} className="center">
              {c.driver_name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {days.map((day) => (
          <tr key={day.date} className={day.sunday ? 'sunday' : undefined}>
            <td style={{ whiteSpace: 'nowrap' }}>
              {Number(month.slice(5))}月{day.day}日
            </td>
            <td className="center">{day.weekday}</td>
            {cols.map(({ loc, c }, i) => {
              const cell = c.cells[day.date];
              return (
                <td key={`${loc}${i}`} className="num" title={cell?.half ? '半日' : cell?.penalty ?? undefined}>
                  {cell ? formatYen(cell.amount) : ''}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2}>計</td>
          {cols.map(({ loc, c }, i) => (
            <td key={`${loc}${i}`} className="num">
              {formatYen(c.sum)}
            </td>
          ))}
        </tr>
        <tr>
          <td colSpan={2}>稼働日数</td>
          {cols.map(({ loc, c }, i) => (
            <td key={`${loc}${i}`} className="num">
              {c.days}
            </td>
          ))}
        </tr>
        <tr>
          <td colSpan={2}>合計</td>
          {d.locations.map((l) => (
            <td key={l.location} colSpan={l.columns.length} className="center" style={{ fontWeight: 700 }}>
              {formatYen(l.sum)}
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  );
}

/** スマホ: 拠点ごとにドライバーの稼働日数・金額(日ごとの表は PC で) */
function BillingList({ d }: { d: RoutineBillingDetail }) {
  return (
    <>
      <p className="hint" style={{ margin: '0 0 6px' }}>
        日ごとの表は PC の画面で見られます。
      </p>
      <table>
        <thead>
          <tr>
            <th>拠点</th>
            <th>ドライバー</th>
            <th>コース</th>
            <th className="num">稼働日数</th>
            <th className="num">金額</th>
          </tr>
        </thead>
        <tbody>
          {d.locations.map((l) => (
            <Fragment key={l.location}>
              {l.columns.map((c, i) => (
                <tr key={`${l.location}${i}`}>
                  <td>{l.location}</td>
                  <td>{c.driver_name}</td>
                  <td>{c.course}</td>
                  <td className="num">{c.days}日</td>
                  <td className="num">{formatYen(c.sum)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}

function SubcontractView({ month, name, d, issueDate }: { month: string; name: string; d: RoutineSubcontractDetail; issueDate: string }) {
  const sum = (f: (p: RoutineSubcontractDetail['people'][number]) => number) => d.people.reduce((a, p) => a + f(p), 0);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', borderBottom: '1px solid var(--ink)', paddingBottom: 4, marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>
          {name} 御中 {monthLabel(month)}分 支払い明細
        </span>
        <span className="hint">出力 {issueDate.replace(/-/g, '/')}</span>
      </div>
      <p style={{ margin: '0 0 10px' }}>
        {d.people.length}人・支払額の合計 <b style={{ fontSize: 16 }}>{formatYen(sum((p) => p.pay))}円</b>
        <span style={{ marginLeft: 16 }}>支払い期日 {d.due_date ? toShortDate(d.due_date) : '—'}</span>
      </p>
      <div className="rt-wide">
        <table className="no-stack routine-statement">
          <thead>
            <tr>
              <th>ドライバー</th>
              <th className="num">稼働数</th>
              <th className="num">報酬</th>
              <th className="num">相殺(保険料・修理積立)</th>
              <th className="num">ペナルティ</th>
              <th className="num">支払額</th>
            </tr>
          </thead>
          <tbody>
            {d.people.map((p) => (
              <tr key={p.driver_name}>
                <td>{p.driver_name}</td>
                <td className="num">{p.days}</td>
                <td className="num">
                  {formatYen(p.reward)}
                  {p.reward_notes.map((n) => (
                    <div key={n} className="hint">
                      {n}
                    </div>
                  ))}
                </td>
                <td className="num">
                  {minus(p.offset)}
                  {p.offset_notes.map((n) => (
                    <div key={n} className="hint">
                      {n}
                    </div>
                  ))}
                </td>
                <td className="num">
                  {minus(p.penalty)}
                  {p.penalty_notes.map((n) => (
                    <div key={n} className="hint">
                      {n}
                    </div>
                  ))}
                </td>
                <td className="num" style={{ fontWeight: 700 }}>
                  {formatYen(p.pay)}
                </td>
              </tr>
            ))}
            <tr className="total">
              <td>合計</td>
              <td className="num">{sum((p) => p.days)}</td>
              <td className="num">{formatYen(sum((p) => p.reward))}</td>
              <td className="num">{minus(sum((p) => p.offset))}</td>
              <td className="num">{minus(sum((p) => p.penalty))}</td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatYen(sum((p) => p.pay))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="rt-narrow">
        <table className="routine-statement">
          <thead>
            <tr>
              <th>ドライバー</th>
              <th className="num">稼働数</th>
              <th className="num">報酬</th>
              <th className="num">相殺(保険料・修理積立)</th>
              <th className="num">ペナルティ</th>
              <th className="num">支払額</th>
            </tr>
          </thead>
          <tbody>
            {d.people.map((p) => (
              <tr key={p.driver_name}>
                <td>{p.driver_name}</td>
                <td className="num">{p.days}</td>
                <td className="num">
                  {formatYen(p.reward)}
                  {p.reward_notes.map((n) => (
                    <div key={n} className="hint">
                      {n}
                    </div>
                  ))}
                </td>
                <td className="num">
                  {minus(p.offset)}
                  {p.offset_notes.map((n) => (
                    <div key={n} className="hint">
                      {n}
                    </div>
                  ))}
                </td>
                <td className="num">
                  {minus(p.penalty)}
                  {p.penalty_notes.map((n) => (
                    <div key={n} className="hint">
                      {n}
                    </div>
                  ))}
                </td>
                <td className="num" style={{ fontWeight: 700 }}>
                  {formatYen(p.pay)}
                </td>
              </tr>
            ))}
            <tr className="total">
              <td>合計</td>
              <td className="num">{sum((p) => p.days)}</td>
              <td className="num">{formatYen(sum((p) => p.reward))}</td>
              <td className="num">{minus(sum((p) => p.offset))}</td>
              <td className="num">{minus(sum((p) => p.penalty))}</td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatYen(sum((p) => p.pay))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="hint" style={{ marginTop: 6 }}>
        支払額 = 報酬 − 相殺 − ペナルティ。稼働数の半日は 0.5日です。
      </p>
    </div>
  );
}

/** ③の通知書の行の並び(Routine の御支払通知書と同じ。* は値があるときだけ出す) */
const YELLOW: Array<{ id: string; label: string; optional?: boolean }> = [
  { id: 'lease', label: 'リース料' },
  { id: 'gas', label: 'ガソリン代', optional: true },
  { id: 'dorm', label: '寮費' },
  { id: 'ins', label: '車両保険料' },
  { id: 'rep', label: '修理積立金' },
];
const BLUE_HEAD: Array<{ id: string; label: string; optional?: boolean }> = [
  { id: 'advance', label: '前渡金' },
  { id: 'fee', label: '手数料' },
  { id: 'phone', label: '携帯電話使用料' },
  { id: 'card', label: 'ガソリンカード貸与手数料', optional: true },
  { id: 'other', label: 'その他控除' },
];

function jaDate(date: string): string {
  if (!date) return '';
  const [y, m, d] = date.split('-').map(Number);
  return `${y}年${m}月${d}日`;
}

function DriverNotice({ month, name, d, issuer, issueDate }: { month: string; name: string; d: RoutineDriverDetail; issuer: Issuer | null; issueDate: string }) {
  const byId = new Map(d.deductions.map((x) => [x.id, x]));
  const known = new Set([...YELLOW, ...BLUE_HEAD].map((r) => r.id).concat('invoice'));
  const row = (id: string, label: string, cls: string) => {
    const x = byId.get(id);
    return (
      <tr key={id} className={cls}>
        <td>{x?.label ?? label}</td>
        <td className="num">{x ? formatYen(x.amount) : ''}</td>
        <td className="hint">{x?.note ?? ''}</td>
      </tr>
    );
  };
  const others = d.deductions.filter((x) => !known.has(x.id));
  return (
    <div className="rt-notice">
      <div className="rt-notice-date">{jaDate(issueDate)}</div>
      <div className="rt-notice-title">
        <span className="rt-notice-month">{monthLabel(month)}度</span>
        <span className="rt-notice-name">御 支 払 通 知 書</span>
      </div>
      <div className="rt-notice-parties">
        <div className="rt-notice-to">{name}　様</div>
        {issuer && (
          <div className="rt-notice-from">
            <div>{issuer.name}</div>
            {issuer.postal_code && <div>〒{issuer.postal_code}</div>}
            {issuer.address && <div>{issuer.address}</div>}
          </div>
        )}
      </div>
      <p className="rt-notice-lead">
        配送作業業務支払いについて
        <br />
        下記の通り通知申し上げます。
      </p>
      <table className="no-stack rt-notice-amount">
        <tbody>
          <tr>
            <th>御支払金額</th>
            <td>¥{formatYen(d.net)}</td>
          </tr>
        </tbody>
      </table>
      <table className="no-stack rt-notice-lines">
        <thead>
          <tr>
            <th>明細</th>
            <th>金額(消費税10%込)</th>
            <th>備考</th>
          </tr>
        </thead>
        <tbody>
          {d.rewards.map((r, i) => (
            <tr key={`r${i}`} className="rt-reward">
              <td>{r.label}</td>
              <td className="num">{formatYen(r.amount)}</td>
              <td className="hint">{r.note}</td>
            </tr>
          ))}
          <tr className="rt-reward">
            <td>報酬額計</td>
            <td className="num">{formatYen(d.reward_total)}</td>
            <td />
          </tr>
          {YELLOW.filter((r) => !r.optional || byId.has(r.id)).map((r) => row(r.id, r.label, 'rt-ded1'))}
          {BLUE_HEAD.filter((r) => !r.optional || byId.has(r.id)).map((r) => row(r.id, r.label, 'rt-ded2'))}
          {others.map((x) => row(x.id, x.label, 'rt-ded2'))}
          {row('invoice', 'インボイス未登録手数料', 'rt-ded2')}
          <tr className="rt-net">
            <td>差引支払金額</td>
            <td className="num">{formatYen(d.net)}</td>
            <td className="hint">{d.invoice_on ? `インボイス未登録(${d.invoice_rate ?? '—'}%)` : ''}</td>
          </tr>
        </tbody>
      </table>
      <p className="hint" style={{ marginTop: 6 }}>稼働 {d.days}日</p>
    </div>
  );
}
